import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { ApiClient } from "~/api/api-client";
import { getCourseQueryKey } from "~/api/queries/useCourse";

import type { CourseAccessLevel, OrderItemOutcome } from "@repo/shared";

/**
 * Lo que devuelve el servidor: además de inscribir, deja una orden (el ledger comercial),
 * así el resumen y "Mis compras" tienen de dónde leer lo que acaba de pasar.
 */
export type DirectEnrollResult = {
  message: string;
  orderId: string | null;
  accessLevel: CourseAccessLevel;
  outcome: OrderItemOutcome;
};

type DirectEnrollInput = {
  courseId: string;
  /** Nivel que se adquiere: completo (curso gratis) o la muestra de un curso pago. */
  accessLevel: CourseAccessLevel;
};

/**
 * Inscripción directa: saltea carrito y checkout cuando no hay nada que cobrar.
 *
 * Es idempotente del lado del servidor, así que un doble click no inscribe dos veces ni
 * genera dos órdenes. Un 409 significa "ya lo tenía", que para esta operación es éxito:
 * el resultado que la persona quería (tener el curso) se cumple igual.
 */
export function useDirectEnroll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ courseId, accessLevel }: DirectEnrollInput) => {
      try {
        const response = await ApiClient.instance.post<{ data: DirectEnrollResult }>(
          `/api/course/${courseId}/enroll`,
          undefined,
          { params: { accessLevel } },
        );
        return response.data?.data ?? null;
      } catch (error) {
        const status = error instanceof AxiosError ? error.response?.status : undefined;
        if (status !== 409) throw error;
        return null;
      }
    },
    onSuccess: (_data, { courseId }) => {
      // El detalle tiene que repintarse con el nivel nuevo: sin esto la pantalla sigue
      // ofreciendo inscribirse a alguien que acaba de inscribirse.
      queryClient.invalidateQueries({ queryKey: getCourseQueryKey(courseId) });
      queryClient.invalidateQueries({ queryKey: ["course"] });
      queryClient.invalidateQueries({ queryKey: ["studentCourses"] });
    },
  });
}
